
import { HttpStatusCodes } from "@/constants/httpStatusCodes";
import { CFormField, validateFormFields } from "@/models/form_cards";
import { API_ROUTES } from "@/utils/API";
import { AxiosError } from "axios";
import { Component, Vue, Watch } from "vue-property-decorator";
import { SMApp, SMNotifications, SMUser } from "@/store";

type TPasswordResetReqData = {
  email: string;
};

type TPasswordResetReqData2 = {
  token: string;
  password_1: string;
  password_2: string;
};
@Component({
  components: {
    FormCard: () => import("@/components/FormCard/FormCard.vue"),
    PasswordRequirements: () =>
      import("@/components/PasswordRequirements/PasswordRequirements.vue"),
  },
})
export default class PasswordReset extends Vue {
  state = {
    isLoading: false,
    isInstructionSent: false,
    isEmailUnknown: false,
    isNewPasswordSet: false,
  };

  get routeQuery() {
    return this.$route.query;
  }

  @Watch("routeQuery", { immediate: true, deep: true })
  async routeQueryChanged(query: any) {
    if (!query?.token) return;

    this.token = query?.token;

    await this.clearSessionForPasswordReset();
    SMApp.unsetPreviousPath();
    SMApp.unsetPreviousUserId();
  }

  async clearSessionForPasswordReset() {
    if (!Vue.auth?.check()) return;

    await Vue.auth.logout({ redirect: false });

    SMUser.unsetUser();
    SMNotifications.stopNotificationsRefresh();
    SMApp.resetAuthOnServer();
  }

  token: string | null = null;

  email = new CFormField("Email")
    .addRuleByAlias("email")
    .addRule(() => !this.state.isEmailUnknown || "Unknown email");

  get isFormValid() {
    const { email } = this;

    return validateFormFields([email]);
  }

  getData(): TPasswordResetReqData {
    const { email } = this;

    return {
      email: email.value,
    };
  }

  showUnknowEmailErrorWithTimer(secCount = 3) {
    this.state.isEmailUnknown = true;

    setTimeout(() => (this.state.isEmailUnknown = false), secCount * 1000);
  }

  handlePasswordResetError(code?: number) {
    if (code === HttpStatusCodes.INTERNAL_SERVER_ERROR) {
      this.showUnknowEmailErrorWithTimer();
    }
  }

  async resetPassword() {
    this.state.isLoading = true;
    const data = this.getData();
    this.axios
      .post(API_ROUTES.PASSWORD_RESET, data)
      .then(() => (this.state.isInstructionSent = true))
      .catch((err: AxiosError) => this.handlePasswordResetError(err?.response?.status))
      .finally(() => (this.state.isLoading = false));
  }

  password1 = new CFormField("Password").addRuleByAlias("password");
  password2 = new CFormField("Confirm password").addRule(
    () => this.doPasswordsMatch || "Password doesn't match"
  );

  get doPasswordsMatch() {
    return this.password1.value === this.password2.value;
  }

  get isFormValid2() {
    const { password1, password2 } = this;

    return validateFormFields([password1, password2]);
  }

  getDataPassword(token: string): TPasswordResetReqData2 {
    const { password1, password2 } = this;
    return {
      token,
      password_1: password1.value,
      password_2: password2.value,
    };
  }

  isError = false;

  async setNewPassword() {
    this.isError = false;

    const { token } = this;

    if (!token) return;

    try {
      this.state.isLoading = true;

      const data = this.getDataPassword(token);

      await this.axios.put(API_ROUTES.PASSWORD_RESET, data);
    } catch {
      this.isError = true;
    } finally {
      this.state.isLoading = false;

      if (!this.isError) {
        this.state.isNewPasswordSet = true;
      }
    }
  }

  goLogin() {
    SMApp.unsetPreviousPath();
    SMApp.unsetPreviousUserId();

    this.$router.push({ name: "Login" });
  }
}
